import React from 'react';
import { View, TouchableOpacity, StyleSheet } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { useTheme } from '../../theme/ThemeContext';
import ThemedText from '../common/ThemedText';
import { LedgerInvite } from '../../lib/api/ledger';

interface PendingInviteCardProps {
  invite: LedgerInvite;
  onAccept: (inviteId: string) => void;
  onReject: (inviteId: string) => void;
}

const PendingInviteCard: React.FC<PendingInviteCardProps> = ({
  invite,
  onAccept,
  onReject,
}) => {
  const { theme } = useTheme();

  return (
    <View style={[styles.container, { backgroundColor: theme.backgroundTertiary, borderColor: theme.accent }]}>
      <View style={styles.header}>
        <View style={[styles.iconContainer, { backgroundColor: theme.accent + '20' }]}>
          <Ionicons name="mail-outline" size={24} color={theme.accent} />
        </View>
        <View style={styles.headerText}>
          <ThemedText style={[styles.title, { color: theme.text }]}>
            Ledger Invite
          </ThemedText>
          <ThemedText style={[styles.subtitle, { color: theme.textSecondary }]}>
            From {invite.fromUserName}
          </ThemedText>
        </View>
        <View style={[styles.badge, { backgroundColor: theme.accent }]}>
          <ThemedText style={[styles.badgeText, { color: theme.textInverse }]}>NEW</ThemedText>
        </View>
      </View>

      <View style={[styles.ledgerInfo, { backgroundColor: theme.card }]}>
        <Ionicons name="book-outline" size={20} color={theme.accent} style={styles.ledgerIcon} />
        <ThemedText style={[styles.ledgerName, { color: theme.text }]}>
          {invite.ledger?.personName || 'Unnamed Ledger'}
        </ThemedText>
      </View>

      <ThemedText style={[styles.message, { color: theme.textSecondary }]}>
        {invite.fromUserName} wants to share this ledger with you. You'll have view-only access to see all transactions.
      </ThemedText>

      <View style={styles.actions}>
        <TouchableOpacity
          style={[styles.rejectButton, { borderColor: theme.border }]}
          onPress={() => onReject(invite.id)}
          activeOpacity={0.7}
        >
          <Ionicons name="close-circle-outline" size={20} color={theme.error} />
          <ThemedText style={[styles.rejectButtonText, { color: theme.error }]}>
            Reject
          </ThemedText>
        </TouchableOpacity>
        
        <TouchableOpacity
          style={[styles.acceptButton, { backgroundColor: theme.accent }]}
          onPress={() => onAccept(invite.id)}
          activeOpacity={0.7}
        >
          <Ionicons name="checkmark-circle-outline" size={20} color={theme.textInverse} />
          <ThemedText style={[styles.acceptButtonText, { color: theme.textInverse }]}>
            Accept
          </ThemedText>
        </TouchableOpacity>
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    marginHorizontal: 16,
    marginVertical: 8,
    padding: 16,
    borderRadius: 16,
    borderWidth: 2,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 3,
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 12,
  },
  iconContainer: {
    width: 40,
    height: 40,
    borderRadius: 20,
    justifyContent: 'center',
    alignItems: 'center',
    marginRight: 12,
  },
  headerText: {
    flex: 1,
  },
  title: {
    fontSize: 16,
    fontWeight: '700',
    marginBottom: 2,
  },
  subtitle: {
    fontSize: 14,
    fontWeight: '500',
  },
  badge: {
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 12,
  },
  badgeText: {
    fontSize: 11,
    fontWeight: '700',
  },
  ledgerInfo: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: 12,
    borderRadius: 12,
    marginBottom: 12,
  },
  ledgerIcon: {
    marginRight: 8,
  },
  ledgerName: {
    fontSize: 15,
    fontWeight: '600',
    flex: 1,
  },
  message: {
    fontSize: 14,
    lineHeight: 20,
    marginBottom: 16,
  },
  actions: {
    flexDirection: 'row',
    gap: 12,
  },
  rejectButton: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 12,
    borderRadius: 12,
    borderWidth: 1,
    gap: 6,
  },
  rejectButtonText: {
    fontSize: 15,
    fontWeight: '600',
  },
  acceptButton: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 12,
    borderRadius: 12,
    gap: 6,
  },
  acceptButtonText: {
    fontSize: 15,
    fontWeight: '600',
  },
});

export default PendingInviteCard;
